import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as importApi from '../services/importApi.ts';
import { formatDate, formatCurrency } from '../utils/dateUtils.ts';
import './Import.css';

type ImportStep = 'upload' | 'review' | 'applied';

interface Account { id: number; name: string; }
interface Category { id: number; name: string; type: string; }

const Import: React.FC = () => {
  const [step, setStep] = useState<ImportStep>('upload');
  const [session, setSession] = useState<importApi.ImportSession | null>(null);
  const [entries, setEntries] = useState<importApi.ImportEntry[]>([]);
  const [applyResult, setApplyResult] = useState<importApi.ApplyResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [editingEntry, setEditingEntry] = useState<number | null>(null);
  const [entryForm, setEntryForm] = useState<importApi.UpdateEntryRequest>({ status: 'pending' });
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [confirmApply, setConfirmApply] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadAccountsAndCategories = async () => {
    const { api } = await import('../services/api.ts');
    const [acc, cat] = await Promise.all([
      api.get<Account[]>('/api/v1/accounts').then(r => r.data),
      api.get<Category[]>('/api/v1/categories').then(r => r.data),
    ]);
    setAccounts(acc);
    setCategories(cat);
  };

  const uploadFile = async (file: File) => {
    if (!file.name.match(/\.(xlsx|csv)$/i)) {
      setError('Apenas arquivos .xlsx e .csv são suportados.');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await importApi.createSession(formData);
      await loadAccountsAndCategories();
      setSession(result.session);
      setEntries(result.entries);
      setStep('review');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao processar o arquivo');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  };

  const handleIgnoreEntry = async (entryId: number) => {
    try {
      const updated = await importApi.updateEntry(session!.id, entryId, { status: 'ignored' });
      setEntries(prev => prev.map(e => e.id === entryId ? updated : e));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao ignorar lançamento');
    }
  };

  const handleIgnoreAllPending = async () => {
    const pending = entries.filter(e => e.status === 'pending');
    try {
      const updated = await Promise.all(
        pending.map(e => importApi.updateEntry(session!.id, e.id, { status: 'ignored' }))
      );
      const updatedMap = new Map(updated.map(e => [e.id, e]));
      setEntries(prev => prev.map(e => updatedMap.get(e.id) ?? e));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao ignorar lançamentos pendentes');
    }
  };

  const openEditEntry = (entry: importApi.ImportEntry) => {
    setEditingEntry(entry.id);
    setEntryForm({
      status: entry.status === 'ignored' ? 'ignored' : 'matched',
      action_type: entry.action_type,
      account_id: entry.account_id,
      category_id: entry.category_id,
      fii_asset_id: entry.fii_asset_id,
      fii_income_type: entry.fii_income_type ?? 'dividendo',
    });
  };

  const handleSaveEntry = async () => {
    if (!session || editingEntry === null) return;
    try {
      const updated = await importApi.updateEntry(session.id, editingEntry, entryForm);
      setEntries(prev => prev.map(e => e.id === editingEntry ? updated : e));
      setEditingEntry(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao salvar lançamento');
    }
  };

  const handleApply = async () => {
    if (!session) return;
    setApplying(true);
    setConfirmApply(false);
    setError('');
    try {
      const result = await importApi.applySession(session.id);
      setApplyResult(result);
      setStep('applied');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao aplicar importação');
    } finally {
      setApplying(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setSession(null);
    setEntries([]);
    setApplyResult(null);
    setError('');
    setEditingEntry(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const pendingCount = entries.filter(e => e.status === 'pending').length;
  const matchedCount = entries.filter(e => e.status === 'matched').length;
  const ignoredCount = entries.filter(e => e.status === 'ignored').length;

  const categoryOptions = (actionType: importApi.ImportActionType | null | undefined) =>
    categories.filter(c => {
      if (actionType === 'receita') return c.type === 'income';
      if (actionType === 'despesa') return c.type === 'expense';
      return true;
    });

  // ── Step 1: Upload ──────────────────────────────────────────────────────

  if (step === 'upload') {
    return (
      <div className="import-page">
        <div className="import-header">
          <h2>Importar Extrato</h2>
          <Link to="/import/rules" className="btn-secondary">⚙️ Gerenciar Regras</Link>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div
          className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.csv"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          {uploading ? (
            <div className="upload-loading">
              <div className="spinner" />
              <span>Processando arquivo...</span>
            </div>
          ) : (
            <>
              <div className="upload-icon">📁</div>
              <div className="upload-text">
                <strong>Arraste um arquivo aqui</strong> ou clique para selecionar
              </div>
              <div className="upload-hint">Suporte: .xlsx e .csv (Extrato XP Investimentos)</div>
            </>
          )}
        </div>

        <div className="import-info">
          <h3>Como funciona</h3>
          <ol>
            <li>Faça upload do seu extrato no formato XLSX ou CSV da XP Investimentos.</li>
            <li>Os lançamentos serão automaticamente mapeados com base nas suas <Link to="/import/rules">regras de importação</Link>.</li>
            <li>Revise os lançamentos pendentes e aplique a importação.</li>
          </ol>
        </div>
      </div>
    );
  }

  // ── Step 2: Review ──────────────────────────────────────────────────────

  if (step === 'review') {
    return (
      <div className="import-page">
        <div className="import-header">
          <h2>Revisar Importação</h2>
          <button className="btn-secondary" onClick={reset}>← Voltar</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Summary bar */}
        <div className="import-summary">
          <div className="summary-item">
            <span className="summary-label">Total</span>
            <span className="summary-value">{entries.length}</span>
          </div>
          <div className="summary-item matched">
            <span className="summary-label">Mapeados</span>
            <span className="summary-value">{matchedCount}</span>
          </div>
          <div className="summary-item pending">
            <span className="summary-label">Pendentes</span>
            <span className="summary-value">{pendingCount}</span>
          </div>
          <div className="summary-item ignored">
            <span className="summary-label">Ignorados</span>
            <span className="summary-value">{ignoredCount}</span>
          </div>
        </div>

        {/* Bulk actions */}
        <div className="import-bulk-actions">
          {pendingCount > 0 && (
            <button className="btn-secondary" onClick={handleIgnoreAllPending}>
              Ignorar todos os pendentes ({pendingCount})
            </button>
          )}
          <button
            className="btn-primary"
            onClick={() => setConfirmApply(true)}
            disabled={applying || matchedCount === 0}
          >
            {applying ? 'Aplicando...' : `Aplicar importação (${matchedCount} lançamentos)`}
          </button>
        </div>

        {/* Confirm modal */}
        {confirmApply && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>Confirmar importação?</h3>
              <p>
                Serão criados <strong>{matchedCount}</strong> lançamentos no sistema.
                {pendingCount > 0 && <> Os <strong>{pendingCount}</strong> pendentes serão ignorados.</>}
              </p>
              <div className="modal-actions">
                <button className="btn-primary" onClick={handleApply}>Confirmar</button>
                <button className="btn-secondary" onClick={() => setConfirmApply(false)}>Cancelar</button>
              </div>
            </div>
          </div>
        )}

        {/* Entries table */}
        <div className="entries-table-wrapper">
          <table className="entries-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Descrição</th>
                <th>Valor</th>
                <th>Status / Ação</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <React.Fragment key={entry.id}>
                  <tr className={`entry-row status-${entry.status}`}>
                    <td className="entry-date">{formatDate(entry.date)}</td>
                    <td className="entry-desc">
                      <div className="entry-raw-desc">{entry.raw_description}</div>
                      {entry.captured_ticker && (
                        <div className="entry-ticker">
                          Ticker: <strong>{entry.captured_ticker}</strong>
                          {entry.fii_shares > 0 && ` · ${entry.fii_shares} cotas`}
                          {entry.action_type === 'rendimento_fii' && !entry.fii_asset_id && (
                            <span className="warning-badge">⚠️ Ticker não encontrado na carteira</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className={`entry-amount ${entry.is_credit ? 'credit' : 'debit'}`}>
                      {entry.is_credit ? '+' : '-'}{formatCurrency(entry.amount)}
                    </td>
                    <td className="entry-status">
                      {entry.status === 'matched' && entry.action_type && (
                        <span className="entry-badge" style={{ backgroundColor: importApi.ACTION_COLORS[entry.action_type] }}>
                          {importApi.ACTION_LABELS[entry.action_type]}
                        </span>
                      )}
                      {entry.status === 'pending' && (
                        <span className="entry-badge pending-badge">Pendente</span>
                      )}
                      {entry.status === 'ignored' && (
                        <span className="entry-badge ignored-badge">Ignorado</span>
                      )}
                    </td>
                    <td className="entry-actions">
                      {entry.status !== 'applied' && entry.status !== 'error' && (
                        <>
                          <button className="btn-icon" onClick={() => openEditEntry(entry)} title="Editar">✏️</button>
                          {entry.status !== 'ignored' && (
                            <button className="btn-icon" onClick={() => handleIgnoreEntry(entry.id)} title="Ignorar">🚫</button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                  {/* Inline edit form */}
                  {editingEntry === entry.id && (
                    <tr className="entry-edit-row">
                      <td colSpan={5}>
                        <div className="entry-edit-form">
                          <div className="form-row">
                            <div className="form-group">
                              <label>Tipo de Ação</label>
                              <select
                                value={entryForm.action_type ?? ''}
                                onChange={e => setEntryForm(f => ({
                                  ...f,
                                  action_type: e.target.value as importApi.ImportActionType || null,
                                  account_id: null,
                                  category_id: null,
                                }))}
                              >
                                <option value="">Selecionar...</option>
                                <option value="rendimento_fii">Rendimento FII</option>
                                <option value="receita">Receita em Conta</option>
                                <option value="despesa">Despesa em Conta</option>
                                <option value="ignorar">Ignorar</option>
                              </select>
                            </div>
                            {(entryForm.action_type === 'receita' || entryForm.action_type === 'despesa') && (
                              <>
                                <div className="form-group">
                                  <label>Conta</label>
                                  <select
                                    value={entryForm.account_id ?? ''}
                                    onChange={e => setEntryForm(f => ({ ...f, account_id: e.target.value ? Number(e.target.value) : null }))}
                                  >
                                    <option value="">Selecione</option>
                                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label>Categoria</label>
                                  <select
                                    value={entryForm.category_id ?? ''}
                                    onChange={e => setEntryForm(f => ({ ...f, category_id: e.target.value ? Number(e.target.value) : null }))}
                                  >
                                    <option value="">Selecione</option>
                                    {categoryOptions(entryForm.action_type).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                  </select>
                                </div>
                              </>
                            )}
                            {entryForm.action_type === 'rendimento_fii' && (
                              <>
                                <div className="form-group">
                                  <label>Tipo de Rendimento</label>
                                  <select
                                    value={entryForm.fii_income_type ?? 'dividendo'}
                                    onChange={e => setEntryForm(f => ({ ...f, fii_income_type: e.target.value as 'dividendo' | 'jcp' }))}
                                  >
                                    <option value="dividendo">Dividendo</option>
                                    <option value="jcp">JCP</option>
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label>Conta <span className="label-optional">(opcional — gera crédito em conta)</span></label>
                                  <select
                                    value={entryForm.account_id ?? ''}
                                    onChange={e => setEntryForm(f => ({ ...f, account_id: e.target.value ? Number(e.target.value) : null }))}
                                  >
                                    <option value="">Sem lançamento em conta</option>
                                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                  </select>
                                </div>
                                {entryForm.account_id && (
                                  <div className="form-group">
                                    <label>Categoria</label>
                                    <select
                                      value={entryForm.category_id ?? ''}
                                      onChange={e => setEntryForm(f => ({ ...f, category_id: e.target.value ? Number(e.target.value) : null }))}
                                    >
                                      <option value="">Selecione</option>
                                      {categoryOptions('receita').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div className="form-actions">
                            <button className="btn-primary" onClick={handleSaveEntry}>Salvar</button>
                            <button className="btn-secondary" onClick={() => setEditingEntry(null)}>Cancelar</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── Step 3: Applied ─────────────────────────────────────────────────────

  return (
    <div className="import-page">
      <div className="import-header">
        <h2>Importação Concluída</h2>
      </div>

      {applyResult && (
        <>
          <div className="apply-result-grid">
            <div className="apply-result-card success">
              <div className="result-number">{applyResult.applied}</div>
              <div className="result-label">Lançamentos criados</div>
            </div>
            <div className="apply-result-card neutral">
              <div className="result-number">{applyResult.ignored}</div>
              <div className="result-label">Ignorados</div>
            </div>
            <div className="apply-result-card muted">
              <div className="result-number">{applyResult.skipped}</div>
              <div className="result-label">Pulados (pendentes)</div>
            </div>
            {applyResult.errors.length > 0 && (
              <div className="apply-result-card error">
                <div className="result-number">{applyResult.errors.length}</div>
                <div className="result-label">Erros</div>
              </div>
            )}
          </div>

          {applyResult.errors.length > 0 && (
            <div className="apply-errors">
              <h3>Lançamentos com Erro</h3>
              {applyResult.errors.map((e, i) => (
                <div key={i} className="apply-error-item">
                  <div className="apply-error-desc">{e.description}</div>
                  <div className="apply-error-msg">{e.error}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div className="apply-nav">
        <button className="btn-primary" onClick={reset}>Nova Importação</button>
        <Link to="/transactions" className="btn-secondary">Ver Transações</Link>
        <Link to="/fii/income" className="btn-secondary">Ver Rendimentos FII</Link>
      </div>
    </div>
  );
};

export default Import;
